/**
 * Home Page
 * Modern, sleek landing page with search, showcase, and resources
 */

'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import SearchBar from '@/components/SearchBar';
import Showcase from '@/components/Showcase';
import NewsletterCTA from '@/components/NewsletterCTA';
import AdsBlock from '@/components/AdsBlock';
import ExtensionBanner from '@/components/ExtensionBanner';
import MaintenanceMode from '@/components/MaintenanceMode';
import { trackSurpriseMe } from '@/lib/analytics';
import { getRemoteConfigValue } from '@/lib/firebase.client';

interface TrendingSearch {
  term: string;
  icon: string;
}

export default function HomePage() {
  const router = useRouter();
  const [trendingSearches, setTrendingSearches] = useState<TrendingSearch[]>([]);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  useEffect(() => {
    // Load Remote Config values
    try {
      // Check maintenance mode
      const maintenanceMode = getRemoteConfigValue('maintenance_mode');
      if (maintenanceMode === 'true') {
        setIsMaintenanceMode(true);
        setMaintenanceMessage(getRemoteConfigValue('maintenance_message'));
        return;
      }

      // Load trending searches
      const trendingData = getRemoteConfigValue('trending_searches');
      if (trendingData) {
        const parsed = JSON.parse(trendingData) as TrendingSearch[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setTrendingSearches(parsed);
        }
      }
    } catch (error) {
      console.error('Error loading Remote Config:', error);
    }
  }, []);

  // Show maintenance mode if enabled
  if (isMaintenanceMode) {
    return <MaintenanceMode message={maintenanceMessage} />;
  }

  const handleSurpriseMe = async () => {
    trackSurpriseMe();
    // Fetch a random suggestion from the API
    try {
      const response = await fetch('/api/vertex?random=true');
      if (response.ok) {
        const data = await response.json();
        if (data.suggestion) {
          router.push(`/results?q=${encodeURIComponent(data.suggestion)}&mode=FSA`);
        }
      }
    } catch (error) {
      console.error('Error fetching random suggestion:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Chrome Extension Banner */}
      <ExtensionBanner variant="hero" />

      {/* Hero Section with Gradient Background */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-primary-50">
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
          <div className="text-center mb-16 animate-fade-in">
            {/* Minimal Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 shadow-sm mb-8">
              <span className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-gray-700">FSA & HSA Eligibility Checker</span>
            </div>

            <h1 className="text-5xl sm:text-7xl font-bold text-gray-900 mb-6 tracking-tight">
              Maximize Your
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-600">
                Healthcare Dollars
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-12 leading-relaxed">
              Instantly verify FSA/HSA eligibility for any product. Backed by IRS guidelines and intelligent analysis.
            </p>

            {/* Search Bar */}
            <div className="mb-8">
              <SearchBar autoFocus />
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>IRS Publication 502 Verified</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>Thousands of Items</span>
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
              </svg>
              <span>Community Powered</span>
            </div>
          </div>
        </div>
      </section>

      {/* AdSense - Home Top */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-12">
        <AdsBlock slot="home_top" format="horizontal" />
      </div>

      {/* Value Props - Clean Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Instant Results</h3>
            <p className="text-gray-600 leading-relaxed">
              Search by name, paste a product URL, or scan a barcode for immediate eligibility verification.
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">IRS Compliant</h3>
            <p className="text-gray-600 leading-relaxed">
              Every result references IRS Publication 502 and includes supporting documentation when needed.
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary-200 hover:shadow-xl transition-all duration-300">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Resources</h3>
            <p className="text-gray-600 leading-relaxed">
              Access our learning hub with guides on FSA vs HSA, LMN requirements, and spending strategies.
            </p>
          </div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Showcase />
        </div>
      </section>

      {/* AdSense - Home Bottom */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 my-12">
        <AdsBlock slot="home_bottom" format="horizontal" />
      </div>

      {/* Popular Searches - More Modern */}
      {trendingSearches.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Trending Searches
            </h2>
            <p className="text-gray-600">Popular items our community is checking</p>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {trendingSearches.map((item) => (
              <a
                key={item.term}
                href={`/results?q=${encodeURIComponent(item.term)}&mode=FSA`}
                className="group flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 rounded-full text-gray-700 hover:border-primary-500 hover:text-primary-600 hover:shadow-md transition-all duration-200 font-medium"
              >
                <span className="text-lg group-hover:scale-110 transition-transform duration-200">{item.icon}</span>
                <span>{item.term}</span>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <NewsletterCTA />
      </section>

      {/* Educational CTA - Modern Design */}
      <section className="bg-gradient-to-br from-primary-500 to-primary-600 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            New to FSA & HSA Accounts?
          </h2>
          <p className="text-xl text-primary-50 mb-8 leading-relaxed">
            Learn the differences, understand LMN requirements, and discover strategies to maximize your tax-advantaged healthcare spending.
          </p>
          <a
            href="/resources"
            className="inline-flex items-center gap-2 bg-white text-primary-600 px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            <span>Explore Learning Hub</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}
